import { Button, Card, Chip, Code, KeyValue, MetricCell, MetricRow, PageHeader, Skeleton, StatusDot, fmtCount, fmtDuration, fmtRelative, fmtTimestamp } from '@agency/ui';
import { RefreshCw } from 'lucide-react';
import { ErrorCallout, Grid, Page } from '../../app/widgets.js';
import { API_BASE } from '../../lib/api.js';
import { COMPONENT_TONE } from '../../lib/labels.js';
import { useApi } from '../../lib/useApi.js';

export function HealthPage() {
  const health = useApi('admin.ops.health', {}, { refreshMs: 30_000 });
  const h = health.data;
  const workerAge = h?.workerLastTickAt ? Date.now() - new Date(h.workerLastTickAt).getTime() : null;
  const workerTone = workerAge === null ? 'dim' : workerAge < 60_000 ? 'ok' : workerAge < 5 * 60_000 ? 'warn' : 'brand';
  return (
    <Page>
      <PageHeader
        eyebrow="System operations"
        eyebrowPath="Health"
        title="Health"
        subtitle={h ? `${h.ok ? 'Healthy' : 'Degraded'} · server ${h.version} · ${h.mode} mode · ${h.db} · up ${fmtDuration(h.uptimeSeconds * 1000)} · worker tick ${h.workerLastTickAt ? fmtRelative(h.workerLastTickAt) : '--'} · outbox ${h.outboxPending} pending, ${h.outboxDead} dead` : 'Loading health.'}
        actions={<Button variant="primary" icon={RefreshCw} loading={health.refreshing} onClick={() => void health.reload()}>Refresh</Button>}
      />
      <ErrorCallout error={health.error} onRetry={() => void health.reload()} className="mt-6" />
      {!h ? (
        <Skeleton height={280} className="mt-6" />
      ) : (
        <Grid>
          <Card title="Components" subtitle={`checked ${fmtTimestamp(h.components[0]?.checkedAt ?? new Date().toISOString(), { seconds: true })}`} span="col-span-12 lg:col-span-7" flush>
            <ul className="divide-y divide-hair">
              {h.components.map((c) => (
                <li key={c.name} className="flex items-center justify-between gap-4 px-5 py-2.5 text-[12.5px]">
                  <StatusDot tone={COMPONENT_TONE[c.status]} mono={false} label={<span className="text-ink">{c.name.replace(/_/g, ' ')}</span>} pulse={c.status === 'down'} />
                  <span className="flex items-center gap-3">
                    <span className="text-mute">{c.detail ?? '--'}</span>
                    <Chip tone={c.status === 'ok' ? 'ok' : c.status === 'degraded' ? 'warn' : c.status === 'down' ? 'brand' : 'mute'}>{c.status}</Chip>
                  </span>
                </li>
              ))}
            </ul>
          </Card>
          <Card title="Server" span="col-span-12 lg:col-span-5">
            <MetricRow columns={3}>
              <MetricCell label="Outbox pending" value={fmtCount(h.outboxPending)} />
              <MetricCell label="Outbox dead" value={fmtCount(h.outboxDead)} tone={h.outboxDead > 0 ? 'brand' : 'ink'} />
              <MetricCell label="Worker" value={h.workerLastTickAt ? fmtRelative(h.workerLastTickAt) : '--'} tone={workerTone === 'ok' ? 'ok' : workerTone === 'warn' ? 'warn' : workerTone === 'brand' ? 'brand' : 'mute'} />
            </MetricRow>
            <KeyValue
              className="mt-4"
              items={[
                { key: 'API', value: API_BASE, mono: true },
                { key: 'Version', value: h.version, mono: true },
                { key: 'Mode', value: h.mode, mono: true },
                { key: 'Database', value: h.db === 'pglite' ? 'PGlite (embedded Postgres)' : 'PostgreSQL', mono: true },
                { key: 'Uptime', value: fmtDuration(h.uptimeSeconds * 1000), mono: true },
                { key: 'Worker last tick', value: h.workerLastTickAt ? fmtTimestamp(h.workerLastTickAt, { seconds: true }) : '--', mono: true },
              ]}
            />
            <div className="mt-5 border-t border-hair pt-4">
              <p className="label-mono">Signing keys</p>
              <ul className="mt-2 divide-y divide-hair">
                {h.signingKeys.length === 0 ? <li className="py-2 text-xs text-mute">No signing keys reported.</li> : h.signingKeys.map((k) => (
                  <li key={`${k.purpose}:${k.kid}`} className="flex items-center justify-between gap-3 py-2 text-[12.5px]">
                    <span className="flex items-center gap-2"><Chip tone="mute">{k.purpose}</Chip><Code>{k.kid}</Code></span>
                    <span className="font-mono text-2xs text-mute">{fmtTimestamp(k.createdAt)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        </Grid>
      )}
    </Page>
  );
}
